import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Lock, FileDown } from "lucide-react";
import { PublicShell, PublicMessage, PublicError } from "@/components/PublicShell";

export const MediadatenLink = () => (
  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/public/mediadaten.pdf`} target="_blank" rel="noreferrer"
    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline shrink-0" data-testid="booking-mediadaten-download">
    <FileDown size={16} /> Mediadaten (PDF)
  </a>
);

const FIELDS = [
  ["name", "Firma *", 6], ["salutation", "Anrede", 2], ["title", "Titel", 1], ["first_name", "Vorname", 3],
  ["last_name", "Nachname", 3], ["position", "Position", 3], ["street", "Straße & Hausnummer", 6], ["zip", "PLZ", 2],
  ["city", "Ort", 4], ["email", "E-Mail *", 3], ["phone", "Telefon", 3], ["mobile", "Mobil", 3], ["website", "Website", 3],
];
const SPAN = { 1: "sm:col-span-1", 2: "sm:col-span-2", 3: "sm:col-span-3", 4: "sm:col-span-4", 6: "sm:col-span-6" };
const euro = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function FormatPicker({ formats, value, onChange, vat }) {
  const info = { vat_rate: vat };
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="booking-formats">
      {formats.map((f) => (
        <button key={f.id} type="button" disabled={f.disabled} onClick={() => onChange(f.id)} data-testid={`booking-format-${f.id}`}
          className={`text-left rounded-lg border p-3 transition-colors ${f.disabled ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed" : value === f.id ? "border-slate-900 ring-2 ring-slate-900 bg-white" : "border-slate-200 hover:border-slate-400 bg-white"}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-sm">{f.name}</span>
            {f.disabled ? <Lock size={14} /> : <span className="text-sm font-semibold text-right">{euro(f.price)}<span className="block text-[10px] font-normal text-slate-500">netto zzgl. {info.vat_rate} % MwSt.</span></span>}
          </div>
          <div className="text-xs mt-0.5">{f.disabled ? "bereits vergeben" : f.width_mm ? `${f.width_mm} × ${f.height_mm} mm${f.bleed_mm ? ` + ${f.bleed_mm} mm Beschnitt` : ""}` : ""}</div>
        </button>
      ))}
    </div>
  );
}

export function BookingForm({ info, url, onDone }) {
  const [c, setC] = useState(info.customer);
  const avail = info.formats.find((f) => f.id === info.ad_format_id && !f.disabled);
  const [fmt, setFmt] = useState(avail ? avail.id : "");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState("");
  const chosen = info.formats.find((f) => f.id === fmt);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!fmt) { setError("Bitte ein Anzeigenformat wählen."); return; }
    setBusy(true);
    try { await api.post(url, { customer: c, format_id: fmt, price_note: note, hp }); onDone(); }
    catch (err) { setError(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-6">
      <input type="text" name="company_website" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <section>
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">1. Anzeigenformat wählen</h2>
        {info.last_invoice && (
          <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-3 py-2" data-testid="booking-prev-year">
            Ihre letzte Buchung: <b>{info.last_invoice.subject}</b> zum Preis von <b>{euro(info.last_invoice.net)}</b> netto zzgl. MwSt..
          </div>
        )}
        <FormatPicker formats={info.formats} value={fmt} onChange={setFmt} vat={info.vat_rate} />
        <p className="text-xs text-slate-500 mt-2" data-testid="booking-vat-hint">Alle Preise verstehen sich netto zuzüglich der gesetzlichen Mehrwertsteuer von derzeit {info.vat_rate} %.</p>
      </section>
      {chosen && (
        <div className="rounded-lg border border-slate-900 bg-slate-50 px-4 py-3 text-sm flex flex-wrap justify-between gap-2" data-testid="booking-selection">
          <span>Ihre Auswahl: <b>{chosen.name}</b>{chosen.width_mm ? ` · ${chosen.width_mm} × ${chosen.height_mm} mm` : ""}</span>
          <span className="font-semibold">{euro(chosen.price)} netto zzgl. {info.vat_rate} % MwSt.</span>
        </div>
      )}
      <section>
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">2. Ihre Kontaktdaten</h2>
        <div className="grid grid-cols-6 gap-3">
          {FIELDS.map(([k, l, s]) => (
            <div key={k} className={`col-span-6 ${SPAN[s]}`}><Label>{l}</Label>
              {k === "salutation" ? (
                <select value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="booking-salutation">
                  {["", ...new Set(["Herr", "Frau", "Divers", c[k]].filter(Boolean))].map((o) => <option key={o} value={o}>{o || "Bitte wählen"}</option>)}
                </select>
              ) : (
              <Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} type={k === "email" ? "email" : "text"} required={k === "name" || k === "email"} className="mt-1" data-testid={`booking-${k}`} />
              )}</div>
          ))}
        </div>
      </section>
      <section>
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">3. Anmerkung zum Preis (optional)</h2>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={info.last_invoice ? `z. B. Preis wie im Vorjahr (${euro(info.last_invoice.net)} netto)` : "z. B. vereinbarter Nachlass oder Sonderpreis aus dem Vorjahr"} data-testid="booking-price-note" />
      </section>
      {error && <div className="text-sm text-red-600" data-testid="booking-error">{error}</div>}
      <p className="text-xs text-slate-500">Mit dem Absenden geht Ihre Buchung bei uns ein. Die Redaktion prüft sie und bestätigt sie Ihnen gesondert.</p>
      <Button type="submit" disabled={busy} className="w-full bg-[#0B132B] hover:bg-[#1C2541] text-white h-11 rounded-lg" data-testid="booking-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : "Anzeige verbindlich buchen"}
      </Button>
    </form>
  );
}

export default function BookingPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/booking/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e.response?.data?.detail))); }, [token]);
  return (
    <PublicShell wide>
      <div data-testid="booking-page">
        {error && <PublicError text={error} testid="booking-invalid" />}
        {!error && !info && <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>}
        {info && (done || info.state === "booked") && <PublicMessage title="Vielen Dank für Ihre Buchung!" text={`Ihre Buchung${info.booking ? ` (${info.booking.format}, eingegangen am ${new Date(info.booking.booked_at).toLocaleDateString("de-DE")})` : ""} liegt uns vor und wird geprüft. Sie erhalten in Kürze eine Rückmeldung per E-Mail. Änderungswünsche teilen Sie uns bitte per Antwort auf unsere Mail mit.`} testid="booking-done" />}
        {info && !done && info.state === "closed" && <PublicMessage icon={Lock} tone="text-amber-500" title="Buchung nicht mehr möglich" text="Für diese Ausgabe ist eine Buchung über diesen Link nicht mehr möglich. Bitte melden Sie sich direkt bei uns." testid="booking-closed" />}
        {info && !done && info.state === "closed" && <div className="text-center"><MediadatenLink /></div>}
        {info && !done && info.state === "open" && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2"><h1 className="font-display text-2xl font-bold text-slate-900">Anzeige buchen – {info.issue}</h1><MediadatenLink /></div>
            <p className="text-sm text-slate-500 mt-1 mb-6">Bitte wählen Sie Ihr Wunschformat und prüfen bzw. ergänzen Sie anschließend Ihre Kontaktdaten.{info.deadline ? ` Buchung möglich bis ${info.deadline.split("-").reverse().join(".")}.` : ""}</p>
            <BookingForm info={info} url={`/public/booking/${token}`} onDone={() => setDone(true)} />
          </>
        )}
      </div>
    </PublicShell>
  );
}
